import { type UseModalState, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CHAT_PAGE_FULL_PATH } from "@src/appV2/Chat/paths";
import { useGetCredentialSharingConfiguration } from "@src/appV2/Facilities/api/useGetCredentialSharingConfiguration";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import {
  addAppStateChangeListener,
  type AppListenerHandle,
} from "@src/appV2/NativeAdapters/app.adapter";
import { WorkplaceNoteIdentifier } from "@src/appV2/redesign/Workplace/constants";
import { useWorkplaceNotesParsed } from "@src/appV2/redesign/Workplace/useWorkplaceNotesParsed";
import { useCreateChatChannel } from "@src/appV2/ShiftBlocks/api/useCreateChatChannel";
import { useGetChartingCredentials } from "@src/appV2/Shifts/Shift/useGetChartingCredentials";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useEffect } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { ChartingCredentialsSetUpBottomSheet } from "./ChartingCredentialsSetUpBottomSheet";
import {
  SHIFT_CREDENTIALS_CONFIRMATION_KEY,
  WORKPLACE_CREDENTIALS_CONFIRMATION_KEY,
} from "./constants";
import { UnableToAccessChartingBottomSheet } from "./UnableToAccessChartingBottomSheet";
import { UpdateChartingCredentialsContainer } from "./UpdateChartingCredentialsContainer";

interface ChartingCredentialsSetUpContainerProps {
  shiftId: string;
  workplaceId: string;
  modalState?: UseModalState;
  onComplete?: () => void;
}

type CredentialsConfirmation = Record<string, boolean>;

export function ChartingCredentialsSetUpContainer({
  shiftId,
  workplaceId,
  modalState,
  onComplete,
}: ChartingCredentialsSetUpContainerProps) {
  const worker = useDefinedWorker();
  const history = useHistory();

  // Modal states
  const setupModalState = useModalState("open");
  const updateModalState = useModalState();
  const unableToAccessModalState = useModalState();

  // Local storage for tracking confirmation
  const [confirmedShifts, setConfirmedShifts] = useLocalStorage<CredentialsConfirmation>(
    SHIFT_CREDENTIALS_CONFIRMATION_KEY,
    {}
  );
  const [confirmedWorkplaces, setConfirmedWorkplaces] = useLocalStorage<CredentialsConfirmation>(
    WORKPLACE_CREDENTIALS_CONFIRMATION_KEY,
    {}
  );

  const { credentialSharingConfiguration } = useGetCredentialSharingConfiguration(workplaceId, {
    enabled: isDefined(workplaceId),
  });
  const { chartingReminders } = credentialSharingConfiguration ?? {};

  // Hooks
  const { data: chartingCredentialsResponse } = useGetChartingCredentials(
    worker.userId,
    workplaceId
  );

  const { mutateAsync: createChatChannel } = useCreateChatChannel();

  const { data: workplaceNotes } = useWorkplaceNotesParsed(workplaceId);
  const chartingInstructions =
    workplaceNotes?.[WorkplaceNoteIdentifier.CHARTING_INSTRUCTIONS]?.note ?? undefined;

  // Extract charting credentials from response
  const chartingCredentials =
    chartingCredentialsResponse?.data?.[0]?.attributes?.chartingCredentials;

  // Check if worker has already confirmed credentials for this shift or workplace
  const hasConfirmedShiftCredentials = confirmedShifts[shiftId];
  const hasConfirmedWorkplaceCredentials = confirmedWorkplaces[workplaceId];

  useEffect(() => {
    if (isDefined(modalState) && modalState.modalIsOpen) {
      setupModalState.openModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setupModalState is recreated on every render
  }, [modalState]);

  const handleLoggedIn = () => {
    // Close setup modal and open update modal
    modalState?.closeModal();
    setupModalState.closeModal();
    // Only show the modal to update if the worker has not previously
    // confirmed credentials for this workplace
    if (!hasConfirmedWorkplaceCredentials) {
      updateModalState.openModal();
      setConfirmedWorkplaces((previous) => ({
        ...previous,
        [workplaceId]: true,
      }));
    }

    // Record that the login has been confirmed
    setConfirmedShifts((previous) => ({
      ...previous,
      [shiftId]: true,
    }));
  };

  useEffect(() => {
    let listenerHandle: AppListenerHandle | undefined;

    async function setupListener() {
      listenerHandle = await addAppStateChangeListener(({ isActive }) => {
        if (isActive && updateModalState.modalIsClosed && unableToAccessModalState.modalIsClosed) {
          setupModalState.openModal();
        }
      });
    }

    void setupListener();

    return () => {
      void listenerHandle?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setupModalState is recreated on every render
  }, []);

  const handleTroubleLoggingIn = () => {
    // Close setup modal and open unable to access modal
    modalState?.closeModal();
    setupModalState.closeModal();
    unableToAccessModalState.openModal();
  };

  const handleSkip = () => {
    // Just close the modal without recording confirmation
    modalState?.closeModal();
    setupModalState.closeModal();
    onComplete?.();
  };

  const handleUpdateCredentialsSuccess = () => {
    onComplete?.();
  };

  const handleMessageWorkplace = async () => {
    try {
      await createChatChannel({
        facilityId: workplaceId,
        agentId: worker.userId,
      });

      const prefilledMessage =
        "Hello, could you please share my username and password for charting? Thank you!";
      const pathname = generatePath(CHAT_PAGE_FULL_PATH, {
        facilityId: workplaceId,
      });
      const params = new URLSearchParams({ prefilledMessage });
      history.push({
        pathname,
        search: `?${params.toString()}`,
      });
    } catch (error) {
      logEvent(APP_V2_USER_EVENTS.CLOCK_IN_CHARTING_CREDENTIALS_CHAT_CHANNEL_ERROR, {
        error: error instanceof Error ? error.message : String(error),
        workerId: String(worker.userId),
        workplaceId,
        shiftId,
      });
    }
  };

  const handleContactSupport = async () => {
    void openZendeskMessaging();
  };

  if (!chartingReminders?.clockIn?.enabled) {
    return null;
  }

  return (
    <>
      {!hasConfirmedShiftCredentials && (
        <ChartingCredentialsSetUpBottomSheet
          modalState={modalState ?? setupModalState}
          shiftId={shiftId}
          workplaceId={workplaceId}
          workerId={worker.userId}
          chartingCredentials={chartingCredentials}
          chartingInstructions={chartingInstructions}
          onLoggedIn={handleLoggedIn}
          onTroubleLoggingIn={handleTroubleLoggingIn}
          onSkip={handleSkip}
        />
      )}

      <UpdateChartingCredentialsContainer
        modalState={updateModalState}
        shiftId={shiftId}
        workplaceId={workplaceId}
        workerId={worker.userId}
        chartingCredentials={chartingCredentials}
        onSuccess={handleUpdateCredentialsSuccess}
        onClose={handleSkip}
      />

      <UnableToAccessChartingBottomSheet
        modalState={unableToAccessModalState}
        shiftId={shiftId}
        workplaceId={workplaceId}
        workerId={worker.userId}
        onMessageWorkplace={handleMessageWorkplace}
        onContactSupport={handleContactSupport}
        onClose={handleSkip}
      />
    </>
  );
}
